import { berlinUhrzeit, type Anrede, type Rolle } from "@dj/domain/gemeinsam";
import { DARSTELLUNG, jagdzustandZu, naechsterWechsel, type Eingriff, type Jagdabschnitt, type Jagdzustand } from "@dj/domain/drueckjagd";
import { useEffect, useState } from "react";
import { useDokument } from "../plattform/lesemodelle";
import { serverJetzt, useServerkontakt, type Serverkontakt } from "./serverzeit";

/**
 * Jagdzustand auf dem Gerät (ADR-0017, ADR-0020): abgeleitet aus dem zuletzt bekannten Jagdzeitplan und Eingriff des
 * Lesemodells `drueckjagden/{id}/jagdzustand/aktuell`, zur Serverzeit (Gerätezeit + zuletzt bekannte Abweichung,
 * ADR-0030), mindestens jede Sekunde neu — mit und ohne Netz nach derselben Regel wie der Server.
 */
export function useServerzeitTakt(ms = 1000): Date {
  const [jetzt, setJetzt] = useState(() => serverJetzt());
  useEffect(() => {
    const t = window.setInterval(() => setJetzt(serverJetzt()), ms);
    return () => window.clearInterval(t);
  }, [ms]);
  return jetzt;
}

export interface LetzterEingriff {
  art: Eingriff["art"];
  name: string;
  anrede: Anrede;
  rolle: Rolle;
  zeitpunkt: Date;
  grund: string | null;
}

export interface Zustandsanzeige {
  zustand: Jagdzustand;
  darstellung: (typeof DARSTELLUNG)[Jagdzustand];
  zeile: string;
  /** Der Jagdzustand ist bekannt (Lesemodell geliefert, auch „nicht vorhanden“); vorher zeigt die App keinen (§4.1) */
  bekannt: boolean;
  /** Farbe der Zustandsfläche (`data-zustand`), solange der Jagdzustand unbekannt ist keine */
  flaeche: string | undefined;
  jetzt: Date;
  naechster: { Zeitpunkt: Date; Jagdzustand: Jagdzustand } | null;
  eingriff: LetzterEingriff | null;
  kontakt: Serverkontakt;
}

export function useJagdzustand(drueckjagdId: string | undefined): Zustandsanzeige {
  const { daten, laedt } = useDokument(drueckjagdId ? `drueckjagden/${drueckjagdId}/jagdzustand/aktuell` : null);
  const kontakt = useServerkontakt();
  useServerzeitTakt();
  const jetzt = serverJetzt();
  const plan = ((daten?.["Jagdzeitplan"] as Jagdabschnitt[] | undefined) ?? []).filter((a) => a.Beginn && a.Ende);
  const e = daten?.["Eingriff"] as { Art?: Eingriff["art"]; Zeitpunkt?: Date } | null | undefined;
  const eingriff: Eingriff | null = e?.Art && e.Zeitpunkt ? { art: e.Art, zeitpunkt: e.Zeitpunkt } : null;
  const zustand: Jagdzustand = jagdzustandZu(jetzt, plan, eingriff);
  const naechster = naechsterWechsel(jetzt, plan, eingriff);
  const l = daten?.["Letzter Eingriff"] as { Art?: Eingriff["art"]; Person?: { Name?: string; Anrede?: Anrede }; Rolle?: Rolle; Zeitpunkt?: Date; Grund?: string | null } | null | undefined;
  const letzter: LetzterEingriff | null = l?.Art && l.Zeitpunkt ? {
    art: l.Art, name: l.Person?.Name ?? "", anrede: l.Person?.Anrede ?? "ohne Anrede", rolle: l.Rolle ?? "Jagdleiter", zeitpunkt: l.Zeitpunkt,
    grund: l.Grund ?? null,
  } : null;
  let zeile = "";
  const laufend = plan.find((a) => a.Beginn <= jetzt && jetzt < a.Ende);
  if (eingriff?.art === "Hahn in Ruh" && zustand === "Jagd ruht") zeile = `seit ${berlinUhrzeit(eingriff.zeitpunkt)} · ausgerufen`;
  else if (laufend && zustand === "Jagd im Gange") zeile = `seit ${berlinUhrzeit(laufend.Beginn)} · bis ${berlinUhrzeit(laufend.Ende)}`;
  else if (naechster) zeile = naechster.Jagdzustand === "Jagd im Gange" ? `Beginn ${berlinUhrzeit(naechster.Zeitpunkt)}` : `bis ${berlinUhrzeit(naechster.Zeitpunkt)}`;
  const bekannt = !!daten || !laedt;
  return { zustand, darstellung: DARSTELLUNG[zustand], zeile, bekannt, flaeche: bekannt ? DARSTELLUNG[zustand].zustand : undefined, jetzt, naechster, eingriff: letzter, kontakt };
}
